import {
  Box,
  buildPath,
  buildTemplatePath,
  CrudMode,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  TabContext,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Partner, PartnerViewResource, Permission } from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import ApiService from "services/apiService";
import { PartnerBasicInfo } from "./PartnerBasicInfo";
import { PartnerPageHeader } from "./PartnerPageHeader";
import { IndirectCosts } from "./IndirectCosts";
import { PartnerTabs } from "./PartnerTabs";
import { SubPartners } from "./SubPartners";
import { useNavigate } from "react-router-dom";

const routeName = "partner";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

class OptionalParams {
  tab?: PartnerTab | undefined = undefined;
}

export const partnerPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Partner",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export type PartnerTab = "Partner" | "Indirect Costs" | "Sub-Partners";

export const PartnerPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const [currentTab, setCurrentTab] = useState<PartnerTab>(optionalParams.tab || "Partner");
  const [partner, setPartner] = useState<Partner>({} as Partner);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [partner] });
  const [viewResource, setViewResource] = useState<PartnerViewResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  // keeps track of which tabs data are loaded
  const [visitedTabs, setVisitedTabs] = useState<PartnerTab[]>([]);

  useEffect(() => {
    fetch(params.id);
    fetchViewResource(params.id);
  }, []);

  // load data from each tab as it's selected
  useEffect(() => {
    if (!visitedTabs.includes(currentTab)) {
      setVisitedTabs(
        produce((draft) => {
          draft.push(currentTab);
        })
      );
    }
  }, [currentTab]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${partnerPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetch = (id: number) => {
    ApiService.partners.get(id).then((response) => {
      setPartner(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.partners.getViewResource(id).then((response) => {
      setViewResource(response);
    });
  };

  return (
    <TabContext value={currentTab}>
      <Page
        isForm
        header={
          <PartnerPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={partnerPageRoute.handle}
            crudMode={params.crudMode}
            fetch={fetch}
            reroute={reroute}
            partner={partner}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
          />
        }
        tabs={<PartnerTabs viewResource={viewResource} setCurrentTab={setCurrentTab} />}
      >
        <Box hidden={currentTab !== "Partner"}>
          <PartnerBasicInfo
            partner={partner}
            setPartner={setPartner}
            viewResource={viewResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
          />
        </Box>

        <Box hidden={currentTab !== "Indirect Costs"} height="100%" overflow="auto">
          <IndirectCosts
            initialLoad={visitedTabs.includes("Indirect Costs")}
            partnerId={params.id}
            onGetList={() => fetchViewResource(params.id)}
          />
        </Box>

        <Box hidden={currentTab !== "Sub-Partners"} height="100%" overflow="auto">
          <SubPartners
            initialLoad={visitedTabs.includes("Sub-Partners")}
            partnerId={params.id}
            onGetList={() => fetchViewResource(params.id)}
          />
        </Box>
      </Page>
    </TabContext>
  );
};
